import { useEffect, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const DELETE_REASONS = ["Mistake", "Duplicate", "Wrong amount", "Refunded", "Other"];

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};

/**
 * Reusable confirm dialog for deleting a payment. Requires a reason.
 * @param {object} payment — the payment doc (must include id, amount, paid_at, creator_username?, brand_name?, reference?)
 * @param {(deletedPayment) => void} onDeleted — called after successful delete
 */
export default function PaymentDeleteDialog({ payment, onOpenChange, onDeleted, contextLabel }) {
  const open = !!payment;
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setNote(""); setBusy(false); }
  }, [open]);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason first"); return; }
    if (reason === "Other" && !note.trim()) { toast.error("Add a short note explaining 'Other'"); return; }
    setBusy(true);
    try {
      // Cost payments live in a separate collection — route accordingly so the cost's
      // paid_amount and payment_status refresh too (cost status will no longer show "Paid")
      const path = payment.source === "Cost" ? `/cost-payments/${payment.id}` : `/payments/${payment.id}`;
      await api.delete(path, { data: { reason, note: note.trim() } });
      toast.success("Payment deleted · audit logged");
      onDeleted?.(payment);
      onOpenChange?.(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !busy && onOpenChange?.(null)}>
      <AlertDialogContent data-testid="delete-payment-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this payment?</AlertDialogTitle>
          <AlertDialogDescription>
            <b>{fmtCur(payment?.amount)}</b>
            {payment?.creator_username ? <> paid to <b>@{payment.creator_username}</b></> : null}
            {contextLabel ? <> ({contextLabel})</> : payment?.brand_name ? <> ({payment.brand_name})</> : null}
            {" "}on <b>{fmtDateTime(payment?.paid_at)}</b>
            {payment?.reference ? <> (ref <span className="font-mono text-xs">{payment.reference}</span>)</> : null}
            . This can't be undone. Pick why you're removing it so admins can review later.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="delete-reason-select">
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {DELETE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Note {reason === "Other" ? "*" : <span className="text-slate-400 normal-case tracking-normal text-[10px]">(optional)</span>}
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={reason === "Other" ? "Explain briefly…" : "Anything the audit log should know?"}
              className="mt-1"
              maxLength={280}
              data-testid="delete-reason-note"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="cancel-delete-payment">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={submit}
            disabled={busy || !reason || (reason === "Other" && !note.trim())}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="confirm-delete-payment"
          >
            {busy ? "Deleting…" : "Delete payment"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
